import { useEffect } from "react";
import { useHeartRate } from "../heart-rate.context";
import type { HeartRateZone } from "../heart-rate.model";

/**
 * THE WHOLE APP TAKES THE GROUND OF THE ZONE THE ATHLETE IS IN, while a strap is live.
 *
 * ═══ WHAT IT IS FOR ═══
 *
 * The timer paints its whole screen in the zone, and that is the one screen where the ground IS
 * the information — readable across a gym by somebody who cannot focus on a number. This is the
 * same fact carried everywhere else: the lift log, the session screen, the debrief. An athlete
 * resting between sets is not looking at the clock, and "am I still in the red" should not
 * require finding the little chip in the header and reading two digits off it.
 *
 * ═══ 🚨 IT IS THE SAME COLOUR AS THE TIMER'S, AND IT USED TO BE A DIFFERENT ONE ═══
 *
 * This was a translucent WASH — 12–25% of the light `-accent` end of each hue laid over the
 * app's near-black ground, on the argument that every card, hairline and muted label in the
 * product is chosen against `--color-background` and would fail contrast on a saturated one.
 * The argument was sound and the result was not: zone four on the timer is burnt orange and
 * zone four everywhere else was a pale warm grey, so the one signal that is supposed to be
 * legible across a room read as two different colours depending on which screen you were on.
 * The owner's report was exactly that — "the same heart-rate background should be visible
 * through the whole app" — and the decision taken was full strength.
 *
 * ═══ 🔑 SO IT SWAPS TOKENS RATHER THAN LAYING ANYTHING OVER ANYTHING ═══
 *
 * A full-strength colour cannot be an overlay: at 100% an overlay is a curtain, and the app is
 * behind it. What changes instead is what `--background` MEANS, on the document root, for as
 * long as the feed is live — so every surface in the product that paints `bg-background`
 * (`__root.tsx`'s frame, the shell, the tab bar's gradient, `body` in `native.css`) takes the
 * zone's own ground, the very token `TIMER_GROUNDS` paints the clock with.
 *
 * ⚠️ AND THE SECONDARY TEXT AND HAIRLINES GO WITH IT, which is not decoration — it is the
 * contrast objection above, answered rather than ignored. `timer-ground.ts` has always carried
 * a `-lifted` pair for exactly this: a coloured ground is LIGHTER than near-black, so the grey
 * that reads as "secondary" on black (#8e8e93) disappears on indigo and on burnt orange. The
 * same two tokens the timer lifts are lifted here, so the app arrives at the ground the clock
 * was designed on instead of at a differently-broken version of it.
 *
 * ⚠️ WHAT IS DELIBERATELY LEFT ALONE: cards, the accent and every filled control. A card is not
 * the background — it is a panel ON it, and keeping it near-black is what makes it still read as
 * a card once the ground is coloured. Repainting the accent would change the colour of every
 * primary button in the product, which is an identity change nobody asked for.
 *
 * ⚠️ INLINE ON THE ROOT ELEMENT, NOT A STYLESHEET RULE. The palette lives in
 * `shared/design-system/styles/`, which is INSTALLED and overwritten wholesale by the next
 * design-system install — a rule written there would be lost without a trace. An inline custom
 * property on `<html>` also beats the `.dark` class rule that defines these tokens, which a
 * stylesheet rule at the same specificity would not.
 */

/**
 * ONE ENTRY PER ZONE, WRITTEN OUT — the same reason `TIMER_GROUNDS` is a table rather than a
 * string built from the zone id. These are `var()` references, so the values still live in the
 * design system's palette and this file holds no colour of its own.
 */
const ZONE_GROUND: Record<HeartRateZone, string> = {
  z1: "var(--ds-hr-z1)",
  z2: "var(--ds-hr-z2)",
  z3: "var(--ds-hr-z3)",
  z4: "var(--ds-hr-z4)",
  z5: "var(--ds-hr-z5)",
};

/**
 * WHAT A COLOURED GROUND TAKES WITH IT. The keys are the design system's own theme slots, so
 * everything downstream of them — `bg-background`, `text-muted-foreground`,
 * `text-foreground-faint`, `border-border` — follows without a single component knowing.
 */
const LIFTED: Readonly<Record<string, string>> = {
  "--muted-foreground": "var(--ds-timer-dim-lifted)",
  "--ds-foreground-faint": "var(--ds-timer-dim-lifted)",
  "--border": "var(--ds-timer-line-lifted)",
};

/** Every property this component owns, so leaving a zone can put all of them back blind. */
const OWNED = ["--background", ...Object.keys(LIFTED)];

export function HeartRateGround() {
  const hr = useHeartRate();

  /* Only a LIVE feed paints. `connecting` and `signal-lost` have no zone to show, and a colour
     left over from the last beat before a dropout is a reading that is no longer true — the
     whole app painted zone-five red off a beat from thirty seconds ago is the same lie as a
     frozen number, in the one channel that can be read from two metres. */
  const zone = hr.status === "live" ? hr.zone : null;

  useEffect(() => {
    const root = document.documentElement;

    /* PUT BACK, NOT REMEMBERED. Nothing else writes these inline, so clearing the override is
       what restores the theme's own value — storing and replaying the previous string would
       bake a stale copy of the palette into the app the day the palette changes. */
    const clear = () => {
      for (const property of OWNED) root.style.removeProperty(property);
      root.removeAttribute("data-hr-zone");
    };

    if (zone === null) {
      clear();
      return;
    }

    root.style.setProperty("--background", ZONE_GROUND[zone]);
    for (const [property, value] of Object.entries(LIFTED)) {
      root.style.setProperty(property, value);
    }
    /* Published for the harness and for anything that ever needs to style off the zone: the
       computed background alone cannot say WHICH zone produced it without matching colours. */
    root.setAttribute("data-hr-zone", zone);

    return clear;
  }, [zone]);

  /* Nothing is rendered. The zone is a property of the app's ground, not an element on top of
     it — and an element on top of it at full strength would be a curtain over the screen. */
  return null;
}
